/**
 * Reach of a single tour.
 *
 * Shows only how many people saw the current revision. There is deliberately no percentage or
 * completion rate: the BFF has no census of a tenant's users, so any denominator would be
 * invented, and `user_tour_tracking` records only that the tour was seen, not that it finished.
 *
 * @module Pages/BUs/Tours
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading, UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import { useTourStats } from '../../Hooks/useTourStats';
import type { Tour } from '../../Types/Tours.types';
import { getDropOffStep, getOutcomeSlices, hasReportedOutcomes } from '../../Utils/tourOutcomes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useProfile } from '@/Auth/contexts/ProfileContext';
import { useActionToast } from '@/Hooks/useActionToast';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { resetTourViewer } from '../../Services/Tours.service';
import { TOUR_RESET_VIEWER } from '../../Constants/permissions';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const bigNumberStyle = css`
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
`;

const metaStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const captionStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const noteStyle = css`
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  line-height: var(--line-height-normal);
  text-wrap: pretty;
`;

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
`;

const viewerListStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  max-height: 260px;
  overflow-y: auto;
  padding-right: 4px;
`;

const viewerRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
`;

const viewerIdStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const viewerMetaStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const spacerStyle = css`
  flex: 1;
`;

/** Same wording as the breakdown above, so a row and a slice never disagree. */
const OUTCOME_LABEL: Record<string, string> = {
  completed: 'Concluiu',
  abandoned: 'Abandonou',
  skipped: 'Pulou',
  unknown: 'Sem registro',
};

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  padding-top: var(--space-m);
  border-top: 1px solid var(--color-divider);
`;

const sectionTitleStyle = css`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const barStyle = css`
  display: flex;
  height: 10px;
  border-radius: var(--radius-round);
  overflow: hidden;
  background: var(--color-surface-sunken);
`;

const barSegmentStyle = css`
  height: 100%;
`;

const legendStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-s) var(--space-m);
`;

const legendItemStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const dotStyle = css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
`;

export interface TourStatsModalProps {
  open: boolean;
  tour?: Tour;
  onClose: () => void;
}

export const TourStatsModal = ({ open, tour, onClose }: TourStatsModalProps): JSX.Element => {
  const { stats, isLoading } = useTourStats(tour?.id ?? null);
  const toast = useActionToast();
  const queryClient = useQueryClient();
  const { profile } = useProfile();
  const { pages: privileges } = usePrivileges();
  // Flag and permission both required — see the note in WhatsNewList for why.
  const canReset =
    privileges.BUSINESS_UNITS.TOURS.RESET_VIEWER &&
    (profile?.permissions?.includes(TOUR_RESET_VIEWER) ?? false);

  const reset = useMutation({
    mutationFn: (platformUserId: number) => resetTourViewer(tour?.id ?? '', platformUserId),
    onSuccess: (_result, platformUserId) => {
      void queryClient.invalidateQueries({ queryKey: ['tours'] });
      toast.success(
        `Usuário ${platformUserId} vai ver de novo`,
        'O tour volta a abrir para essa pessoa no próximo acesso.',
      );
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível reexibir o tour', error);
    },
  });
  const slices = getOutcomeSlices(stats?.byStatus);
  const viewers = stats?.viewers ?? [];
  const history = stats?.history ?? [];
  const dropOff = getDropOffStep(stats?.stepsHistogram);

  const content = (
    <div className={bodyStyle}>
      {isLoading ? (
        <UTLoading type='dots' />
      ) : (
        <div className={metaStyle}>
          <span className={bigNumberStyle}>{(stats?.viewCount ?? 0).toLocaleString('pt-BR')}</span>
          <span className={captionStyle}>
            {stats?.viewCount === 1 ? 'usuário viu' : 'usuários viram'} a revisão v
            {stats?.version ?? tour?.version} do tour de {tour?.pageSlug}
          </span>
        </div>
      )}

      {!isLoading && hasReportedOutcomes(stats?.byStatus) ? (
        <div className={sectionStyle}>
          <span className={sectionTitleStyle}>Como saíram</span>

          <div className={barStyle}>
            {slices
              .filter(slice => slice.count > 0)
              .map(slice => (
                <span
                  key={slice.key}
                  className={barSegmentStyle}
                  style={{ width: `${slice.percentage}%`, background: slice.color }}
                  title={`${slice.label}: ${slice.count}`}
                />
              ))}
          </div>

          <div className={legendStyle}>
            {slices.map(slice => (
              <span key={slice.key} className={legendItemStyle}>
                <span className={dotStyle} style={{ background: slice.color }} />
                {slice.label} · <strong>{slice.count}</strong> ({slice.percentage}%)
              </span>
            ))}
          </div>

          {dropOff ? (
            <span className={captionStyle}>
              A maior parada é no passo <strong>{dropOff.stepsSeen}</strong>, onde {dropOff.viewers}{' '}
              {dropOff.viewers === 1 ? 'pessoa parou' : 'pessoas pararam'}.
            </span>
          ) : null}
        </div>
      ) : null}

      {!isLoading && !hasReportedOutcomes(stats?.byStatus) ? (
        <div className={noteStyle}>
          Ainda não há registro de como as pessoas saíram deste tour. O dado passa a ser coletado
          depois que o dashboard sobe com a versão que o reporta — visualizações anteriores a isso
          não voltam.
        </div>
      ) : null}

      {!isLoading && history.length > 1 ? (
        <div className={sectionStyle}>
          <span className={sectionTitleStyle}>Por revisão</span>
          <div className={viewerListStyle}>
            {history.map(entry => {
              const entrySlices = getOutcomeSlices(entry.byStatus);

              return (
                <div key={entry.version} className={viewerRowStyle}>
                  <span className={viewerIdStyle}>v{entry.version}</span>
                  {entry.isCurrent ? (
                    <StatusTag status='success' size='sm'>
                      atual
                    </StatusTag>
                  ) : null}
                  <div className={spacerStyle} />
                  <span className={viewerMetaStyle}>
                    {entry.viewCount.toLocaleString('pt-BR')}{' '}
                    {entry.viewCount === 1 ? 'pessoa' : 'pessoas'} ·{' '}
                    {entrySlices
                      .filter(slice => slice.count > 0)
                      .map(slice => `${slice.label.toLowerCase()} ${slice.count}`)
                      .join(', ') || 'sem desfecho registrado'}
                  </span>
                </div>
              );
            })}
          </div>
          <span className={captionStyle}>
            Revisões que ninguém chegou a ver não aparecem: subir a revisão só incrementa um
            contador, então o registro de quem viu é a única prova de que ela existiu.
          </span>
        </div>
      ) : null}

      {!isLoading && viewers.length > 0 ? (
        <div className={sectionStyle}>
          <span className={sectionTitleStyle}>Quem viu</span>
          <div className={viewerListStyle}>
            {viewers.map(viewer => (
              <div key={viewer.platformUserId} className={viewerRowStyle}>
                <span className={viewerIdStyle}>#{viewer.platformUserId}</span>
                <span className={viewerMetaStyle}>
                  {OUTCOME_LABEL[viewer.status ?? 'unknown']}
                  {viewer.stepsSeen === null || viewer.stepsSeen === undefined
                    ? ''
                    : ` · ${viewer.stepsSeen} passo(s)`}
                </span>
                <div className={spacerStyle} />
                {canReset ? (
                  <IconButton
                    id={`tour-viewer-reset-${viewer.platformUserId}`}
                    icon='Arrows.ArrowCounterClockwise'
                    size='sm'
                    disabled={reset.isPending}
                    aria-label={`Fazer o usuário ${viewer.platformUserId} ver o tour de novo`}
                    onClick={() => {
                      reset.mutate(viewer.platformUserId);
                    }}
                  />
                ) : null}
              </div>
            ))}
          </div>
          <span className={captionStyle}>
            São os identificadores dos usuários da plataforma — nome e e-mail ficam no backend de
            contas, não aqui.
          </span>
        </div>
      ) : null}

      <div className={noteStyle}>
        Os números do topo são sempre da revisão atual. Subir a revisão reexibe o tour para todo
        mundo e zera essa contagem — as anteriores ficam em <strong>Por revisão</strong>.
      </div>

      {/*
        Spelled out because it is not obvious from the screen: reporting hangs off the BFF's
        pending-tours map, and a tour leaves that map once it has been seen. So a re-watch reports
        nothing at all — the numbers describe first contact, and cannot drift afterwards.
      */}
      <div className={noteStyle}>
        Cada pessoa conta uma vez, pelo <strong>primeiro contato</strong> com esta revisão. Quem
        reassiste pelo botão de ver o tour de novo não altera o próprio registro — só uma revisão
        nova recomeça a medição.
      </div>

      <div className={footerStyle}>
        <UTButton fitContent text='Fechar' onClick={onClose} />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title='Alcance do tour'
      content={content}
      maxWidth='480px'
      hasButtonClose
      position='center'
    />
  );
};
